/**
 * Ouverture du stockage du poste : Storage Bucket persistant quand Chrome le
 * permet, sinon la base IndexedDB par defaut (plan, section 1.2).
 *
 * Regle de choix, pour ne jamais couper les donnees du poste en deux :
 *   1. une base `caisse-secours` existe deja dans le stockage par defaut
 *      (poste enrole avant que le navigateur connaisse les buckets) : on
 *      continue de s'en servir ;
 *   2. sinon, si l'API Storage Buckets existe : bucket `caisse-secours`,
 *      persistant, durabilite stricte ;
 *   3. si le bucket refuse de s'ouvrir alors qu'il existe deja, la caisse ne
 *      demarre pas : se rabattre sur une base vide ferait croire que le poste
 *      n'est pas enrole, et ses ventes resteraient cachees dans le bucket ;
 *   4. sinon, stockage par defaut.
 */
import { openDatabase, StorageError } from './idb';
import { DB_NAME, DB_VERSION, MIGRATIONS } from './schema';

export const BUCKET_NAME = DB_NAME;
export const BUCKET_OPTIONS = Object.freeze({ persisted: true, durability: 'strict' });

async function defaultDatabaseExists(factory) {
    if (!factory || typeof factory.databases !== 'function') return false;
    try {
        const databases = await factory.databases();
        return databases.some((d) => d && d.name === DB_NAME);
    } catch (_error) {
        return false;
    }
}

async function bucketAlreadyExists(buckets) {
    try {
        const names = await buckets.keys();
        return Array.isArray(names) ? names.includes(BUCKET_NAME) : true;
    } catch (_error) {
        // Impossible de savoir : on suppose qu'il existe, par prudence.
        return true;
    }
}

/**
 * @param {object} [options]
 * @param {Navigator} [options.nav]
 * @param {IDBFactory} [options.defaultFactory]
 * @param {() => void} [options.onVersionChange]
 * @returns {Promise<{ db: IDBDatabase, location: 'bucket'|'default', bucket: object|null }>}
 */
export async function openCaisseStorage({
    nav = globalThis.navigator,
    defaultFactory = globalThis.indexedDB,
    onVersionChange,
} = {}) {
    const buckets = nav && nav.storageBuckets && typeof nav.storageBuckets.open === 'function'
        ? nav.storageBuckets
        : null;
    if (!defaultFactory && !buckets) {
        throw new StorageError('IndexedDB est indisponible dans ce navigateur', 'NO_INDEXEDDB');
    }

    let factory = defaultFactory;
    let bucket = null;
    let location = 'default';

    if (buckets && !(await defaultDatabaseExists(defaultFactory))) {
        try {
            bucket = await buckets.open(BUCKET_NAME, BUCKET_OPTIONS);
            factory = bucket.indexedDB;
            location = 'bucket';
        } catch (error) {
            if (!defaultFactory || await bucketAlreadyExists(buckets)) {
                throw new StorageError('Le stockage du poste est momentanement indisponible', 'BUCKET_UNAVAILABLE', error);
            }
            bucket = null;
        }
    }

    const db = await openDatabase({
        factory,
        name: DB_NAME,
        version: DB_VERSION,
        migrations: MIGRATIONS,
        onVersionChange,
    });
    return { db, location, bucket };
}

/**
 * Demande le stockage persistant et dit s'il est obtenu.
 * Avec un bucket, c'est la persistance DU BUCKET qui compte (c'est lui qui
 * garde les donnees) ; sinon, celle de l'origine (navigator.storage).
 * Une erreur vaut « non persistant » : l'avertissement s'affiche.
 *
 * @returns {Promise<boolean>}
 */
export async function ensurePersistence({ nav = globalThis.navigator, bucket = null } = {}) {
    const storage = nav && nav.storage;
    let originPersisted = false;
    try {
        if (storage && typeof storage.persisted === 'function') {
            originPersisted = (await storage.persisted()) === true;
        }
        if (!originPersisted && storage && typeof storage.persist === 'function') {
            originPersisted = (await storage.persist()) === true;
        }
    } catch (_error) {
        originPersisted = false;
    }
    if (!bucket) return originPersisted;

    try {
        if ((await bucket.persisted()) === true) return true;
        if (typeof bucket.persist === 'function') {
            return (await bucket.persist()) === true;
        }
        return false;
    } catch (_error) {
        return false;
    }
}
